<template>
  <div>
    <!-- Hero Section -->
    <section class="py-5 bg-white" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8" class="text-center">
            <h1 class="display-4 font-weight-bold marketing-heading mb-4">
              Simple, Transparent Pricing
            </h1>
            <p class="lead marketing-body mb-4">
              Choose the plan that fits your business. All plans include a 30-day free trial with no credit card required.
            </p>

            <!-- Billing Toggle -->
            <div class="d-inline-flex align-items-center bg-white rounded shadow-sm p-1" style="border: 1px solid #E2E8F0;">
              <CButton
                @click="billingCycle = 'monthly'"
                :class="billingCycle === 'monthly' ? 'btn-marketing-primary' : ''"
                :color="billingCycle === 'monthly' ? '' : 'light'"
                :variant="billingCycle === 'monthly' ? '' : 'ghost'"
                class="px-4 py-2"
              >
                Monthly
              </CButton>
              <CButton
                @click="billingCycle = 'yearly'"
                :class="billingCycle === 'yearly' ? 'btn-marketing-primary' : ''"
                :color="billingCycle === 'yearly' ? '' : 'light'"
                :variant="billingCycle === 'yearly' ? '' : 'ghost'"
                class="px-4 py-2 position-relative"
              >
                Yearly
                <CBadge class="position-absolute bg-marketing-accent text-white" style="top: -8px; right: -8px;">
                  Save 20%
                </CBadge>
              </CButton>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Pricing Cards -->
    <section class="py-5">
      <CContainer>
        <CRow class="justify-content-center">
          <!-- Starter Plan -->
          <CCol cols="12" md="4" class="mb-4">
            <CCard class="border h-100 shadow-sm hover-lift">
              <CCardBody class="p-4">
                <div class="text-center mb-4">
                  <h3 class="h4 font-weight-bold text-dark mb-2">Starter</h3>
                  <p class="text-muted small">Perfect for small businesses</p>
                </div>

                <div class="text-center mb-4">
                  <div class="d-flex align-items-end justify-content-center mb-2">
                    <span class="display-3 font-weight-bold text-dark">
                      {{ billingCycle === 'monthly' ? '99' : '79' }}
                    </span>
                    <span class="text-muted ml-2 mb-2">RM/month</span>
                  </div>
                  <p v-if="billingCycle === 'yearly'" class="small text-muted">
                    Billed RM 948/year
                  </p>
                  <p v-else class="small text-muted">
                    Billed monthly
                  </p>
                </div>

                <ul class="list-unstyled mb-4">
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Up to <strong>5 employees</strong></span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Invoicing & quotations</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Basic payroll (EPF/SOCSO)</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Expense tracking</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Basic reports</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Email support</span>
                  </li>
                </ul>

                <CButton class="btn-marketing-accent btn-scale" block to="/signup?plan=starter">
                  Start Free Trial
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Professional Plan (Most Popular) -->
          <CCol cols="12" md="4" class="mb-4">
            <CCard class="h-100 shadow position-relative hover-lift" style="border: 2px solid var(--marketing-primary);">
              <CBadge class="position-absolute bg-marketing-accent text-white" style="top: -12px; left: 50%; transform: translateX(-50%);">
                Most Popular
              </CBadge>

              <CCardBody class="p-4">
                <div class="text-center mb-4">
                  <h3 class="h4 font-weight-bold text-dark mb-2">Professional</h3>
                  <p class="text-muted small">For growing businesses</p>
                </div>

                <div class="text-center mb-4">
                  <div class="d-flex align-items-end justify-content-center mb-2">
                    <span class="display-3 font-weight-bold text-dark">
                      {{ billingCycle === 'monthly' ? '199' : '159' }}
                    </span>
                    <span class="text-muted ml-2 mb-2">RM/month</span>
                  </div>
                  <p v-if="billingCycle === 'yearly'" class="small text-muted">
                    Billed RM 1,908/year
                  </p>
                  <p v-else class="small text-muted">
                    Billed monthly
                  </p>
                </div>

                <ul class="list-unstyled mb-4">
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Up to <strong>20 employees</strong></span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark"><strong>Everything in Starter</strong></span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Full accounting (P&L, Balance Sheet)</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Inventory management</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Advanced reports & analytics</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Multi-user access</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Priority support</span>
                  </li>
                </ul>

                <CButton class="btn-marketing-accent btn-scale" block to="/signup?plan=professional">
                  Start Free Trial
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Enterprise Plan -->
          <CCol cols="12" md="4" class="mb-4">
            <CCard class="border h-100 shadow-sm hover-lift">
              <CCardBody class="p-4">
                <div class="text-center mb-4">
                  <h3 class="h4 font-weight-bold text-dark mb-2">Enterprise</h3>
                  <p class="text-muted small">For larger organizations</p>
                </div>

                <div class="text-center mb-4">
                  <div class="d-flex align-items-end justify-content-center mb-2">
                    <span class="display-3 font-weight-bold text-dark">Custom</span>
                  </div>
                  <p class="small text-muted">
                    Contact us for pricing
                  </p>
                </div>

                <ul class="list-unstyled mb-4">
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark"><strong>Unlimited employees</strong></span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark"><strong>Everything in Professional</strong></span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Custom integrations (API)</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Dedicated account manager</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">Custom training & onboarding</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">SLA guarantee</span>
                  </li>
                  <li class="d-flex align-items-start mb-3">
                    <CIcon name="cil-check-circle" size="lg" class="text-marketing-accent mr-2 mt-1"/>
                    <span class="text-dark">24/7 phone support</span>
                  </li>
                </ul>

                <CButton class="btn-outline-light-custom btn-scale" style="border-color: var(--marketing-primary); color: var(--marketing-primary); background: white;" block to="/contact?plan=enterprise">
                  Contact Sales
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- Trust Message -->
        <CRow class="mt-4">
          <CCol cols="12" class="text-center">
            <p class="marketing-body">
              All plans include: 30-day free trial • No credit card required • Cancel anytime • Free updates
            </p>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Feature Comparison Table -->
    <section class="py-5 bg-marketing-light">
      <CContainer>
        <h2 class="h3 font-weight-bold marketing-heading mb-4 text-center">
          Detailed Feature Comparison
        </h2>

        <div class="table-responsive">
          <CTable striped bordered hover class="bg-white">
            <thead class="thead-light">
              <tr>
                <th class="px-4 py-3">Features</th>
                <th class="px-4 py-3 text-center">Starter</th>
                <th class="px-4 py-3 text-center bg-marketing-primary text-white">Professional</th>
                <th class="px-4 py-3 text-center">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="px-4 py-3 font-weight-medium">Number of Employees</td>
                <td class="px-4 py-3 text-center text-muted">Up to 5</td>
                <td class="px-4 py-3 text-center bg-light">Up to 20</td>
                <td class="px-4 py-3 text-center text-muted">Unlimited</td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Invoicing & Quotations</td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center bg-light">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Payroll (EPF/SOCSO/EIS)</td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center bg-light">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Expense Tracking</td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center bg-light">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Full Accounting (Chart of Accounts)</td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-x-circle" class="text-secondary"/>
                </td>
                <td class="px-4 py-3 text-center bg-light">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Inventory Management</td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-x-circle" class="text-secondary"/>
                </td>
                <td class="px-4 py-3 text-center bg-light">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Financial Reports (P&L, Balance Sheet)</td>
                <td class="px-4 py-3 text-center text-muted small">Basic</td>
                <td class="px-4 py-3 text-center bg-light text-dark small">Advanced</td>
                <td class="px-4 py-3 text-center text-dark small">Advanced + Custom</td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Multi-User Access</td>
                <td class="px-4 py-3 text-center text-muted small">1 user</td>
                <td class="px-4 py-3 text-center bg-light text-dark small">5 users</td>
                <td class="px-4 py-3 text-center text-dark small">Unlimited</td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Custom Integrations (API)</td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-x-circle" class="text-secondary"/>
                </td>
                <td class="px-4 py-3 text-center bg-light">
                  <CIcon name="cil-x-circle" class="text-secondary"/>
                </td>
                <td class="px-4 py-3 text-center">
                  <CIcon name="cil-check-circle" class="text-marketing-accent"/>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 font-weight-medium text-dark">Support</td>
                <td class="px-4 py-3 text-center text-muted small">Email</td>
                <td class="px-4 py-3 text-center bg-light text-dark small">Priority Email</td>
                <td class="px-4 py-3 text-center text-dark small">24/7 Phone</td>
              </tr>
            </tbody>
          </CTable>
        </div>
      </CContainer>
    </section>

    <!-- FAQs -->
    <section class="py-5">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8">
            <h2 class="h3 font-weight-bold marketing-heading mb-4 text-center">
              Frequently Asked Questions
            </h2>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">What's included in the free trial?</h6>
                <p class="text-muted mb-0">
                  All features of your chosen plan are fully accessible during the 30-day free trial. No credit card required. You can cancel anytime with no obligations.
                </p>
              </CCardBody>
            </CCard>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">Can I switch plans later?</h6>
                <p class="text-muted mb-0">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges or credits.
                </p>
              </CCardBody>
            </CCard>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">What payment methods do you accept?</h6>
                <p class="text-muted mb-0">
                  We accept all major credit cards (Visa, Mastercard, Amex), online banking (FPX), and monthly invoicing for Enterprise customers.
                </p>
              </CCardBody>
            </CCard>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">Is my data secure?</h6>
                <p class="text-muted mb-0">
                  Absolutely. We use bank-level 256-bit SSL encryption, daily backups, and host on secure Malaysian servers. Your data is never shared with third parties.
                </p>
              </CCardBody>
            </CCard>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">Do you offer training?</h6>
                <p class="text-muted mb-0">
                  Professional plan includes video tutorials and guides. Enterprise customers receive personalized onboarding and training sessions with our team.
                </p>
              </CCardBody>
            </CCard>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">What if I exceed my employee limit?</h6>
                <p class="text-muted mb-0">
                  We'll notify you when you're approaching your limit. You can easily upgrade to a higher plan to accommodate more employees at any time.
                </p>
              </CCardBody>
            </CCard>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">Can I export my data?</h6>
                <p class="text-muted mb-0">
                  Yes! All plans allow you to export your data in Excel, PDF, or CSV format at any time. Your data is always yours.
                </p>
              </CCardBody>
            </CCard>

            <CCard class="mb-3">
              <CCardBody>
                <h6 class="font-weight-semibold text-dark mb-2">Is GST/SST included in the pricing?</h6>
                <p class="text-muted mb-0">
                  Prices shown are before SST. Service tax (6%) applies to all subscription plans in accordance with Malaysian tax regulations.
                </p>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Final CTA -->
    <section class="py-5 bg-marketing-dark text-white" style="background: linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%);">
      <CContainer class="text-center">
        <h2 class="h3 font-weight-bold mb-3" style="color: white;">
          Ready to Get Started?
        </h2>
        <p class="lead mb-4" style="color: rgba(255, 255, 255, 0.9);">
          Join 500+ Malaysian businesses using Niagaku. Start your free trial today - no credit card required.
        </p>
        <div class="d-flex flex-column flex-sm-row justify-content-center">
          <CButton class="btn-marketing-accent mr-sm-2 mb-2 mb-sm-0 px-5 py-3" size="lg" to="/signup">
            Start Free 30-Day Trial
          </CButton>
          <CButton class="btn-outline-light-custom mr-sm-2 mb-2 mb-sm-0 px-5 py-3" size="lg" to="/contact">
            Talk to Sales
          </CButton>
        </div>
      </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Pricing',
  metaInfo: {
    title: 'Pricing - Niagaku Accounting & Payroll Software',
    meta: [
      {
        name: 'description',
        content: 'Simple, transparent pricing for Malaysian businesses. Plans starting from RM 99/month with 30-day free trial. No credit card required.'
      }
    ]
  },
  data() {
    return {
      billingCycle: 'monthly'
    }
  }
}
</script>

<style scoped>
/* Pricing page uses CoreUI components and Bootstrap 4 classes */
</style>
